import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { elasticLogValues, splunkApi1 } from '../../utils/Api'
import axios from 'axios';

// Architecture diagram sources/destinations (from ConfluentArchitecture)
const sources = [
  { icon: "🔥", label: "Firewalls", top: 80 },
  { icon: "🛡️", label: "EDR/XDR", top: 190 },
  { icon: "☁️", label: "Cloud", top: 300 },
  { icon: "📊", label: "SIEM Agents", top: 410 },
  { icon: "💻", label: "Applications", top: 520 },
  { icon: "🌐", label: "Network", top: 630 },
  { icon: "🔐", label: "Identity", top: 740 },
  { icon: "📧", label: "Email", top: 850 },
];
const destinations = [
  { logo: "https://cdn.ivaluetv.com/oem/splunk.svg", label: "Splunk", top: 150, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/elastic.png", label: "Elastic", top: 260, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/OpenText_logo.svg", label: "ArcSight", top: 370, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/Chronicle-RGB-Horz-Color.webp", label: "Google Chronicle", top: 480, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/cloudera-newco-logo.png", label: "Data Lake", top: 620, color: "#8b5cf6" },
];


// Architecture diagram for Path 4 only (Archive)
function ArchitecturePath4() {
  const diagramRef = useRef();
  const svgRef = useRef();
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const wrapper = diagramRef.current;
    if (!wrapper) return;
    svg.setAttribute("width", wrapper.offsetWidth);
    svg.setAttribute("height", wrapper.offsetHeight);
    // Source/dest positions
    const srcs = sources.map((s) => ({ x: 90, y: s.top + 40 }));
    const dsts = destinations.map((dst) => ({ x: wrapper.offsetWidth - 90, y: dst.top + 40 }));
    const center = { x: wrapper.offsetWidth / 2, y: wrapper.offsetHeight / 2 };
    // Store path elements for animation
    const animatedPaths = [];
    // Path 4: Archive (purple)
    srcs.forEach((source) => {
      animatedPaths.push(createPath(source.x, source.y, center.x - 80, center.y - 60, "#8b5cf6", "solid", 1.8, 0.8));
    });
    // Only connect to Data Lake (last destination)
    if (dsts.length > 0) {
      animatedPaths.push(createPath(center.x + 80, center.y - 60, dsts[dsts.length - 1].x, dsts[dsts.length - 1].y, "#8b5cf6", "solid", 2.5, 0.9));
    }
    // Add animated "sending light"
    let animationFrame;
    let t = 0;
    function animate() {
      t += 0.012;
      animatedPaths.forEach(({ path, color, width, light }) => {
        if (light.parentNode) light.parentNode.removeChild(light);
        const pathLength = path.getTotalLength();
        const pos = path.getPointAtLength((pathLength * ((t + Math.random() * 0.2) % 1)));
        light.setAttribute('cx', pos.x);
        light.setAttribute('cy', pos.y);
        svg.appendChild(light);
      });
      animationFrame = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
    // Helper to create path and animated light
    function createPath(x1, y1, x2, y2, color, style = "solid", width = 2, opacity = 1) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const ctrl1x = x1 + dx * 0.3;
      const ctrl1y = y1 + dy * 0.1;
      const ctrl2x = x1 + dx * 0.7;
      const ctrl2y = y2 - dy * 0.1;
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      const d = `M ${x1} ${y1} C ${ctrl1x} ${ctrl1y}, ${ctrl2x} ${ctrl2y}, ${x2} ${y2}`;
      path.setAttribute("d", d);
      path.setAttribute("stroke", color);
      path.setAttribute("stroke-width", width);
      path.setAttribute("fill", "none");
      path.setAttribute("opacity", opacity);
      path.setAttribute("stroke-dasharray", style === "dashed" ? "10 5" : "0");
      path.style.filter = `drop-shadow(0 0 6px ${color})`;
      path.style.transition = "opacity 0.5s ease";
      svg.appendChild(path);
      // Animated light
      const light = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      light.setAttribute('r', Math.max(4, width * 1.7));
      light.setAttribute('fill', color);
      light.setAttribute('opacity', 0.85);
      light.style.filter = `drop-shadow(0 0 12px ${color})`;
      return { path, color, width, light };
    }
  }, []);
  return (
    <div
      ref={diagramRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 1000,
        maxHeight: 1000,
        background: 'rgba(10,34,64,0.92)',
        borderRadius: 18,
        boxShadow: '0 4px 24px 0 rgba(30,144,255,0.10)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg ref={svgRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
      {/* Central Confluent Hub */}
      <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src="https://cdn.ivaluetv.com/oem/Confluent.png" alt="Confluent Logo" style={{ width: 'auto', height: 60, objectFit: 'contain', filter: 'invert(1) brightness(2)' }} />
        <div style={{ color: '#e0e7ef', fontWeight: 700, fontSize: 18, marginTop: 8, textShadow: '0 1px 6px #000' }}>Confluent Hub</div>
      </div>
      {/* Log Sources (Left) */}
      {sources.map((src, i) => (
        <div key={src.label} style={{ position: 'absolute', left: 10, top: src.top, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
          <div style={{ border: '2px solid #60a5fa', color: '#60a5fa', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, background: '#0a2240', boxShadow: '0 2px 8px #1e3a5f' }}>
            <span>{src.icon}</span>
          </div>
          <div style={{ marginTop: 8, color: '#e0e7ef', fontWeight: 500, fontSize: 12, textAlign: 'center', textShadow: '0 1px 6px #000', width: '100%' }}>{src.label}</div>
        </div>
      ))}
      {/* Destinations (Right) - Only Data Lake */}
      {destinations.slice(-1).map((dst, i) => (
        <div key={dst.label} style={{ position: 'absolute', right: 10, top: dst.top, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
          <div style={{ border: `2px solid ${dst.color}`, color: dst.color, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', boxShadow: `0 2px 8px ${dst.color}` }}>
            <img src={dst.logo} alt={dst.label + ' logo'} style={{ width: 36, height: 36, objectFit: 'contain', filter: `drop-shadow(0 0 8px ${dst.color})` }} />
          </div>
          <div style={{ marginTop: 8, color: '#e0e7ef', fontWeight: 500, fontSize: 12, textAlign: 'center', textShadow: '0 1px 6px #000', width: '100%' }}>{dst.label}</div>
        </div>
      ))}
    </div>
  );
}

function Path4() {
 
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });

  const [elasticData, setElasticData] = useState({});
  const [confluentRawIndex, setConfluentRawIndex] = useState(null);

  useEffect(() => {
    const fetchElasticData = async () => {
      try {
        const response = await axios.get(elasticLogValues);
        if (response.data.indices && Array.isArray(response.data.indices)) {
          const indices = response.data.indices;
          
          // Find confluent_raw index and set to elasticData1
          const rawIndex = indices.find(index => index.index === 'confluent_raw');
          if (rawIndex) {
            setElasticData(rawIndex);
          }
          
        }
      } catch (error) {
        console.error("Error fetching Elastic data:", error);
      }
    };

    const fetchSplunkData = async () => {
       try {
        const response = await axios.get(splunkApi1, {});
         const found = response.data.entry.find(e => e.name === 'confluent_1_index_raw');
        setConfluentRawIndex(found || null);
      } catch (error) {
        console.error('Error fetching data from Splunk API:', error);
      }
    };
    fetchElasticData();
    fetchSplunkData();
  }, []);
  
  
  const metrics = [
    { label: 'Storage Size Of Splunk', value: confluentRawIndex ? ((Number(confluentRawIndex.content?.currentDBSizeMB) || 0)/1024).toFixed(2) +' GB' : 'N/A', color: '#00b4a0' },
    { label: 'Storage Size Of Elastic', value: elasticData?.storeSize || 'Loading...', color: '#1e90ff' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', color: 'white', py: 6, px: { xs: 1, sm: 4 }, position: 'relative' }}>
      {/* User name at top right */}
      {user && user.name && (
        <Typography sx={{ position: 'absolute', top: 24, right: 32, fontWeight: 700, fontSize: 18, color: '#e0f7fa', letterSpacing: 0.5 }}>
          Welcome, {user.name}
        </Typography>
      )}
      <Typography variant="h3" sx={{ mb: 3, fontWeight: 600, letterSpacing: -1, background: 'linear-gradient(90deg, #00b4a0 0%, #1e90ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        Path 4 (Archive) Dashboard
      </Typography>
      {/* Section 1: Metrics + Architecture */}
      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
        {/* Left: Metrics */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            {metrics.map((m, i) => (
              <Grid item xs={6} key={m.label}>
                <Card sx={{ background: 'rgba(10,34,64,0.85)', borderRadius: 3, boxShadow: 3, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CardContent sx={{ textAlign: 'center', width: '100%', p: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: m.color, fontWeight: 700 }}>{m.label}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 1, color: 'white' }}>{m.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        {/* Right: Architecture Path 4 */}
        <Grid item xs={12} md={7}>
          <ArchitecturePath4 />
        </Grid>
      </Grid>
    <Box sx={{ height: { xs: 32, sm: 48, md: 64 } }} />
    </Box>
  );
}

export default Path4;
